import clsx from "clsx";
import { StatDelta } from "../StatDelta/StatDelta.js";
import { compactNumber } from "../util/format.js";
import styles from "./StatBlock.module.scss";

export interface StatBlockProps {
  /** The uppercased tile label (e.g. "Spotify streams"). */
  label: string;
  /** The metric. **This component OWNS the null → `—` contract** — a null/undefined value renders `—`,
   * NEVER a fallback number (leak-test contract).*/
  value: number | null | undefined;
  /** Optional signed percent change → a {@link StatDelta} chip (omitted when the value is null). */
  delta?: number | null;
  /** A glyph before the value (e.g. `D` for "day in cycle" →).*/
  prefix?: string;
  /** A glyph after the value (e.g. `%`). */
  suffix?: string;
  /** Render the exact grouped number (`1,234`) instead of the compact `1.2k` form. */
  exact?: boolean;
  /**
   * How the stat is arranged:
   * - `tile` (default) — label above value, for a KPI strip scanned ACROSS a card.
   * - `showcase` — label left, value right on one baseline, one type rung up, for a two-column list read
   *   DOWN a stylised card (the release showcase). The null → `—` contract, the tabular numerals and the
   *   label tier are identical; only the arrangement and the scale differ, which is why this is a variant
   *   rather than a second component.
   *
   * Arrangement and type scale are ONE prop because every caller wants them together. Split them the day a
   * caller genuinely wants one without the other — four states with three unexercised is a contract that
   * 20+ existing call sites have to keep working for no reader's benefit.
   */
  layout?: "tile" | "showcase";
}

/**
 * A KPI stat tile: a small uppercase label over a large tabular-numeral value, with an optional delta
 * chip. The value is null-degraded to `—` HERE so no caller can bypass the leak-test contract by rendering its
 * own fallback number (the `<Stat null>` contract).
 */
export function StatBlock({
  label, value, delta, prefix, suffix, exact, layout = "tile",
}: StatBlockProps) {
  const empty = value == null || !Number.isFinite(value);
  return (
    <div className={clsx(layout === "showcase" ? styles.showcase : styles.tile)}>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>
        {empty ? (
          "—"
        ) : (
          <>
            {prefix}
            {exact ? value!.toLocaleString() : compactNumber(value!)}
            {suffix}
          </>
        )}
      </span>
      {!empty && delta != null && <StatDelta value={delta} className={styles.deltaChip} />}
    </div>
  );
}
